import { QueryHookOptions } from '@apollo/client/react/types/types';
import { Label, LabelAssignment } from '@provision/entity-types';
import { gql, Reference, useMutation, useQuery } from '@provision/graphql-client';
import { useCallback, useEffect, useMemo } from 'react';
import { groupBy, isNullish, uniqueBy } from 'remeda';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { AppEvents, emitAppEvent, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import { LABEL_FIELDS } from './label';

export const LABEL_ASSIGNMENT_FIELDS = gql`
  fragment LabelAssignmentFields on LabelAssignment {
    id
    labelAssignmentId
    labelId
    entity
    entityId
    partnerId
    createdAt
  }
`;

const GET_LABEL_ASSIGNMENT_QUERY = gql`
  query GetLabelAssignment($params: QueryParams) @entity(name: "LabelAssignment") {
    getLabelAssignment(queryParams: $params) {
      records {
        ...LabelAssignmentFields

        label {
          ...LabelFields
        }
      }
    }
  }
  ${LABEL_ASSIGNMENT_FIELDS}
  ${LABEL_FIELDS}
`;

export const DELETE_LABEL_ASSIGNMENT_MUTATION = gql`
  mutation DeleteLabelAssignment($conditions: [ConditionInput]) {
    deleteLabelAssignment(conditions: $conditions) {
      ...LabelAssignmentFields
    }
  }
  ${LABEL_ASSIGNMENT_FIELDS}
`;

export const CREATE_LABEL_ASSIGNMENT_MUTATION = gql`
  mutation CreateLabelAssignment($data: LabelAssignmentDataInput) {
    createLabelAssignment(data: $data) {
      ...LabelAssignmentFields
    }
  }
  ${LABEL_ASSIGNMENT_FIELDS}
`;

export const onlyPublicLabelsFilter = {
  attribute: 'label/isPublic',
  operator: '=',
  value: 'true',
};

const getEntityTypename = (entity: string) =>
  entity.toLowerCase() === 'address'
    ? 'Address'
    : entity.charAt(0).toUpperCase() + entity.slice(1);

const getEntityCacheId = (
  cache: { identify: (value: { __typename: string; id: string }) => string | undefined },
  entity: string,
  entityId: string,
) =>
  cache.identify({
    __typename: getEntityTypename(entity),
    id: entityId,
  });

export const useLabelAssignment = () => {
  const { showErrorAlert } = useNotifications();

  const [deleteLabelAssignmentMutation] = useMutation<{
    deleteLabelAssignment: Partial<LabelAssignment>[];
  }>(DELETE_LABEL_ASSIGNMENT_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong removing label assignment, please try again');
    },
  });

  const deleteLabelAssignment = useCallback(
    async ({
      entity,
      entityId,
      labelIds,
      partnerId,
      canAccessToPrivateLabels: _canAccessToPrivateLabels,
    }: {
      entity: string;
      entityId: string;
      labelIds?: string | string[];
      partnerId?: number;
      canAccessToPrivateLabels?: boolean;
    }) => {
      if (!labelIds?.length) {
        return;
      }
      const conditions = [
        {
          attribute: 'entity',
          operator: '=',
          value: entity,
        },
        {
          attribute: 'entityId',
          operator: '=',
          value: entityId,
        },
        {
          attribute: 'labelId',
          operator: 'in',
          value: Array.isArray(labelIds) ? labelIds.sort().join(',') : labelIds,
        },
      ];

      if (!isNullish(partnerId)) {
        conditions.push({
          attribute: 'partnerId',
          operator: '=',
          value: partnerId.toString(),
        });
      }

      // Emit event optimistically before mutation
      const labelIdsArray = Array.isArray(labelIds) ? labelIds : [labelIds];
      labelIdsArray.forEach((labelId) => {
        emitAppEvent(AppEvents.LabelAssignmentChanged, {
          entity,
          entityId,
          labelId,
          operation: 'delete',
        });
      });

      const { data } = await deleteLabelAssignmentMutation({
        variables: {
          conditions,
        },
        update(cache) {
          const id = getEntityCacheId(cache, entity, entityId);

          if (id) {
            cache.modify({
              id,
              fields: {
                labelAssignments(existingItems = [], { readField }) {
                  const existingLabelAssignments = existingItems as Reference[];
                  return existingLabelAssignments.filter((labelAssignment) => {
                    const labelAssignmentLabelId = readField('labelId', labelAssignment);
                    if (Array.isArray(labelIds)) {
                      return !labelIds.some((l) => l === labelAssignmentLabelId);
                    }

                    return labelAssignmentLabelId !== labelIds;
                  });
                },
              },
            });
          }
        },
        optimisticResponse: {
          deleteLabelAssignment: Array.isArray(labelIds)
            ? labelIds.map((labelId) => ({
                entity,
                entityId,
                labelId,
                partnerId,
                createdAt: new Date().toISOString(),
              }))
            : [
                {
                  entity,
                  entityId,
                  labelId: labelIds,
                  partnerId,
                  createdAt: new Date().toISOString(),
                },
              ],
        },
      });

      return data?.deleteLabelAssignment;
    },
    [deleteLabelAssignmentMutation],
  );
  const [createLabelAssignmentMutation] = useMutation<{
    createLabelAssignment: Partial<
      LabelAssignment & {
        __typename: string;
      }
    >[];
  }>(CREATE_LABEL_ASSIGNMENT_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert(
        'Something went wrong adding a new label to conversation, please try again',
      );
    },
  });

  const createLabelAssignment = useCallback(
    async ({
      entity,
      entityId,
      label,
      partnerId,
      canAccessToPrivateLabels: _canAccessToPrivateLabels,
    }: {
      entity: string;
      entityId: string;
      label: Partial<Label>;
      partnerId?: number;
      canAccessToPrivateLabels?: boolean;
    }) => {
      if (!label.labelId) {
        showErrorAlert('Label ID is required to create an assignment');
        return;
      }

      const createdAt = new Date().toISOString();
      const tempId = `temp-id${createdAt}`;

      const tempAssignment: Partial<LabelAssignment & { __typename: string }> = {
        __typename: 'LabelAssignment',
        id: tempId,
        labelAssignmentId: tempId,
        entity,
        entityId,
        labelId: label.labelId,
        partnerId: partnerId ?? label.partnerId,
        createdAt,
        label: label as Label,
      };

      // Emit event optimistically before mutation
      emitAppEvent(AppEvents.LabelAssignmentChanged, {
        entity,
        entityId,
        labelId: label.labelId.toString(),
        operation: 'create',
        assignment: tempAssignment,
      });

      const { data } = await createLabelAssignmentMutation({
        variables: {
          data: {
            entity,
            entityId,
            labelId: label.labelId,
            partnerId,
          },
        },
        update(cache, { data }) {
          if (!data?.createLabelAssignment?.length) return;
          if (!data.createLabelAssignment[0].labelId) return;

          const createdElement = data?.createLabelAssignment[0];

          const id = getEntityCacheId(cache, entity, entityId);
          if (!id) return;

          cache.modify({
            id,
            fields: {
              labelAssignments(existingItems = [], { toReference }) {
                const existingLabelAssignments = existingItems as Reference[];

                const newLabelAssignment = {
                  ...createdElement,
                  label: toReference({
                    ...label,
                    __typename: 'Label',
                  }),
                };

                const reference = toReference(newLabelAssignment, true);

                if (reference) {
                  return [reference, ...existingLabelAssignments];
                }

                return existingLabelAssignments;
              },
            },
          });
        },
        optimisticResponse: {
          createLabelAssignment: [
            {
              __typename: 'LabelAssignment',
              id: tempId,
              labelAssignmentId: tempId,
              entity,
              entityId,
              labelId: label.labelId,
              partnerId: partnerId ?? label.partnerId,
              createdAt,
            },
          ],
        },
      });

      return data?.createLabelAssignment;
    },
    [createLabelAssignmentMutation, showErrorAlert],
  );

  return {
    createLabelAssignment,
    deleteLabelAssignment,
  };
};

interface GetLabelAssignment {
  getLabelAssignment: Records<LabelAssignment>;
}

export const useEntityAssignments = ({
  entity,
  ids: rawIds,
  filters,
  skip,
  includeDeleted = false,
}: {
  entity?: string;
  ids?: string[] | string;
  filters?: ConditionInput[];
  skip?: boolean;
  includeDeleted?: boolean;
}) => {
  const baseFilters = useMemo(() => {
    const ids = Array.isArray(rawIds) ? rawIds : [rawIds];

    return [
      {
        attribute: 'entity',
        operator: '=',
        value: entity ?? '',
      },
      {
        attribute: 'entityId',
        operator: 'in',
        value: ids?.join(',') ?? '',
      },
    ];
  }, [entity, rawIds]);

  const memoizedFilters = useMemo(() => filters, [filters]);

  const options = useMemo<QueryHookOptions>(() => {
    return {
      variables: {
        params: {
          filters: [...baseFilters, ...(memoizedFilters ?? [])],
          includeDeleted,
        },
      },
      context: {
        includePlatformLevelData: true,
      },
      skip: !entity || !rawIds?.length || skip,
    };
  }, [baseFilters, memoizedFilters, includeDeleted, entity, rawIds?.length, skip]);

  const queryResult = useQuery<GetLabelAssignment, QueryVariables>(
    GET_LABEL_ASSIGNMENT_QUERY,
    options,
  );

  const cacheManager = useCacheManager<GetLabelAssignment, QueryVariables>(
    GET_LABEL_ASSIGNMENT_QUERY,
    queryResult,
  );

  // Subscribe to label assignment changes and update cache optimistically
  useEffect(() => {
    if (skip || !entity || !rawIds?.length) {
      return;
    }

    const unsubscribe = subscribeToAppEvent(AppEvents.LabelAssignmentChanged, (payload) => {
      const ids = Array.isArray(rawIds) ? rawIds : [rawIds];
      // Only update if this event is relevant to our query
      if (
        payload.entity === entity &&
        ids.includes(payload.entityId) &&
        (!memoizedFilters?.some((f) => f.attribute === 'label/isPublic') ||
          payload.assignment?.label?.isPublic !== false)
      ) {
        cacheManager.update((draft: GetLabelAssignment) => {
          if (!draft?.getLabelAssignment) return;

          if (payload.operation === 'create' && payload.assignment) {
            const existingAssignments = new Set(
              draft.getLabelAssignment.records
                .map((r) => `${r.entityId}:${r.labelId?.toString()}`)
                .filter(Boolean),
            );
            const assignmentKey = `${payload.entityId}:${payload.labelId?.toString()}`;
            if (!existingAssignments.has(assignmentKey)) {
              draft.getLabelAssignment.records.push(payload.assignment as LabelAssignment);
            }
          } else if (payload.operation === 'delete') {
            draft.getLabelAssignment.records = draft.getLabelAssignment.records.filter(
              (r) =>
                !(
                  r.entityId === payload.entityId &&
                  r.labelId?.toString() === payload.labelId?.toString()
                ),
            );
          }
        });
      }
    });

    return unsubscribe;
  }, [entity, rawIds, memoizedFilters, skip, cacheManager]);

  const [labelAssignments, labelAssignmentsMap] = useMemo(() => {
    const list = queryResult.data?.getLabelAssignment?.records ?? [];

    const byEntity = groupBy(list, (item) => item.entityId);

    const map = new Map(
      Object.entries(byEntity).map(([entityId, items]) => [
        entityId,
        uniqueBy(items, (item) => item.labelId),
      ]),
    );

    return [list, map];
  }, [queryResult.data?.getLabelAssignment?.records]);

  return { labelAssignments, labelAssignmentsMap };
};
